<template>
    <div v-if="alertType !== 'none'" class="alert-div-body" :class="[alertType, { inline: inline }]">
        <i v-if="alertType === 'alert-warning'" class="fas fa-exclamation-triangle"></i>
        <i v-else-if="alertType === 'alert-danger'" class="fas fa-times-circle"></i>
        <i v-else-if="alertType === 'alert-success'" class="fas fa-check-circle"></i>
        <i v-else-if="alertType === 'alert-info'" class="fas fa-info-circle"></i>

        <div class="alert-div-text">
            <div class="alert-div-text-title">{{ alertTitle || alertTitleDefault }}</div>
            <div class="alert-div-text-message">
                <RichText :text="alertMessage" />
            </div>
            <div v-if="$slots.default && !inline" class="alert-div-actions">
                <slot></slot>
            </div>
        </div>
        <div v-if="$slots.default && inline" class="alert-div-actions">
            <slot></slot>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import RichText from "@/components/ui/RichText.vue";

export default defineComponent({
    components: {
        RichText,
    },
    name: "AlertBox",
    props: {
        alertType: {
            type: String as PropType<"alert-info" | "alert-success" | "alert-danger" | "alert-warning" | "none">,
            default: "none",
        },
        alertTitle: {
            type: String,
            default: "",
        },
        alertMessage: {
            type: String,
            default: "Are you sure you want to proceed? This action cannot be undone.",
        },
        inline: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        alertTitleDefault(): string {
            switch (this.alertType) {
                case "alert-warning":
                    return this.$t("Warning");
                case "alert-danger":
                    return this.$t("Danger");
                case "alert-success":
                    return this.$t("Success");
                case "alert-info":
                    return this.$t("Info");
                default:
                    return "";
            }
        },
    },
});
</script>

<style scoped>
.alert-div-body {
    margin: 10px 0px;
    padding: 20px;
    display: flex;
    justify-content: start;
    margin-bottom: 16px;
    width: 100%;
    border-radius: var(--rounded-lg, 8px);
}

.alert-div-body.alert-warning {
    border: var(--border-width-base, 1px) solid var(--color-border-warning, #ffd230);
    background: var(--color-background-warning-subtler, #fef3c6);
    color: var(--color-text-warning-on-bg, #bb4d00);
}

.alert-div-body.alert-danger {
    border: var(--border-width-base, 1px) solid var(--color-border-danger, #ff5f5f);
    background: var(--color-background-danger-subtler, #ffe1e1);
    color: var(--color-text-danger-on-bg, #a80000);
}

.alert-div-body.alert-success {
    border: var(--border-width-base, 1px) solid var(--color-border-success, #4bb543);
    background: var(--color-background-success-subtler, #d4f5d4);
    color: var(--color-text-success-on-bg, #006400);
}

.alert-div-body.alert-info {
    border: var(--border-width-base, 1px) solid var(--color-border-info, #3b82f6);
    background: var(--color-background-info-subtler, #dbeafe);
    color: var(--color-text-info-on-bg, #003380);
}

.alert-div-body :deep(.rich-text-featured) {
    font-weight: 700;
}

.alert-div-body i {
    font-size: 20px;
}

.alert-div-body .alert-div-text {
    margin-left: 12px;
    display: flex;
    flex-direction: column;
    justify-content: start;
    width: 100%;
    font-size: 20px !important;
}

.alert-div-body .alert-div-text-title {
    font-weight: 600;
    font-size: 16px !important;
    margin-bottom: 8px;
}

.alert-div-body .alert-div-text-message {
    font-size: 14px !important;
}

.alert-div-body .alert-div-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
}

.alert-div-body.inline {
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.alert-div-body.inline > i {
    align-self: flex-start;
}

.alert-div-body.inline .alert-div-text {
    flex: 1;
    margin-left: 0;
    min-width: 0;
}

.alert-div-body.inline .alert-div-actions {
    margin-top: 0;
    margin-left: auto;
    flex-shrink: 0;
}
</style>
